import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { documentTranslationListApi } from 'models/localization/documentTranslation/Model.DocumentTranslation';
import ProductDocument from 'models/productManagement/productDocument/Model.ProductDocument';
import { useState } from 'react';
import { productDocumentIndexRoute } from './ProductDocument.Index';

export enum ProductDocumentTabs {
  DEALERS = 'dealers',
  DEALER_GROUPS = 'dealerGroups',
}
export type VerifyProductDocumentTabs = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Tabs,
  Utils.ValueOf<typeof ProductDocumentTabs>
>;
export default function useProductDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductDocument.Params,
    Module.ProductManagement.ProductDocument.Query
  >({
    route: productDocumentIndexRoute,
  });
  const productDocumentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const [selectedTab, setSelectedTab] = useState<ProductDocumentTabs>(ProductDocumentTabs.DEALERS);

  const { payload: productDocument, isExecuting: isLoadingProductDocument } = useApi(
    {
      action: ProductDocument.find,
      body: { documentId: productDocumentId },
      default: null,
      wait: !productDocumentId,
    },
    [productDocumentId]
  );

  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductDocument', documentId: [productDocumentId] },
      wait: !productDocumentId,
    },
    [productDocumentId]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const canView = permissions.EDIT_PRODUCT;

  return {
    canView,
    selectedTab,
    pageRouter,
    productDocument: productDocument,
    isLoadingProductDocument,
    setSelectedTab,
    permissions,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    views: {
      [ProductDocumentTabs.DEALERS]: selectedTab === ProductDocumentTabs.DEALERS,
      [ProductDocumentTabs.DEALER_GROUPS]: selectedTab === ProductDocumentTabs.DEALER_GROUPS,
    },
  };
}

export function useProductDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductDocument>>('productDocumentHook');
}
